// Reset-password — where the emailed link lands. Takes the token from the
// query string and a new password, then hands off to /login. The token is
// re-validated by the backend on submit (not on page load) — there's nothing
// useful to check ahead of time, and probing it early would just be another
// way to test whether a token is live.
import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { resetPassword } from "../../api/auth";
import emtlogo from "../../images/logo.svg";
import Icon from "../../components/em/Icon";

const MIN_PASSWORD_LENGTH = 8;

const ResetPassword = () => {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!token) {
      setError("This reset link is missing its token. Request a new one.");
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError("The two passwords don't match.");
      return;
    }

    setSubmitting(true);
    try {
      await resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Could not reset your password. The link may have expired.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-bg-wrap">
      <video
        className="login-bg-video"
        src="/videos/login-bg.mp4"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        aria-hidden="true"
      />
      <div className="login-bg-overlay" aria-hidden="true" />
      <div className="container">
        <div className="login-page">
          <div className="item">
            <div className="signin-wrap">
              <div className="signin-form">
                <div className="elogo elogo-inside">
                  <img src={emtlogo} alt="Logo" />
                </div>

                {done ? (
                  <div className="title">
                    <h3>Password reset</h3>
                    <p>Your password has been updated. Taking you to sign in…</p>
                  </div>
                ) : (
                  <>
                    <div className="title">
                      <h3>Reset password</h3>
                      <p>Choose a new password for your account.</p>
                    </div>
                    <div className="login-form">
                      <form onSubmit={handleSubmit} className="loginwrap">
                        <div className="form-item eye-icon">
                          <label>New Password</label>
                          <input
                            name="password"
                            placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                            type={showPassword ? "text" : "password"}
                            autoComplete="new-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                          />
                          <div
                            className="pass-show"
                            onClick={() => setShowPassword((v) => !v)}
                          >
                            <Icon name={showPassword ? "eye" : "eyeslash"} />
                          </div>
                        </div>

                        <div className="form-item eye-icon">
                          <label>Confirm Password</label>
                          <input
                            name="confirm"
                            placeholder="Type it again"
                            type={showConfirm ? "text" : "password"}
                            autoComplete="new-password"
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            required
                          />
                          <div
                            className="pass-show"
                            onClick={() => setShowConfirm((v) => !v)}
                          >
                            <Icon name={showConfirm ? "eye" : "eyeslash"} />
                          </div>
                        </div>

                        {error && (
                          <p
                            style={{
                              color: "#e7000b",
                              font: "400 12px/1.5 inherit",
                              margin: "6px 0 0",
                            }}
                          >
                            {error}
                          </p>
                        )}

                        <div className="form-action">
                          <button
                            type="submit"
                            className="btn-orange"
                            disabled={submitting}
                          >
                            {submitting ? "Saving…" : "Reset password"}
                          </button>
                        </div>

                        <p className="siup-txt">
                          <Link to="/login">Back to sign in</Link>
                        </p>
                      </form>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
